import ContentLayout from '@/components/layout/ContentLayout';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { MultiSelect } from '@/components/multi-select';
import { CascadingSelect, type CascadingOption } from '@/components/cascading-select';
import { Step, StepConnector } from '@/components/stepper';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { ROUTE } from '@/routes/constants';
import { useGetAllClassInfo, useGetTopicList, useGetKnowledgePointList, getSchoolPress, generateQuestion, createQuestionInfo, listTeachersBySchool } from '@/services/generatedAPIs';
import { useSubjectList } from '@/hooks/use-subject-list';
import { getSubjectDisplayName } from '@/hooks/use-subject-name';
import { ClassroomInfoResponse, QuestionInfoExt } from '@/services/generatedAPIs.schemas';
import { Press, QuestionOrigin } from '@/utils/constants';
import { isEN } from '@/utils/i18n';
import { PopoverClose } from '@radix-ui/react-popover';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { HomeworkDraft, getHomeworkDrafts, removeHomeworkDraft } from '@/utils/homework-draft-utils';
import { Trash2, Play, CircleHelp } from 'lucide-react';
import { difficultyLevelToText, gradeNumberToText } from '@/lib/utils';

export type AssignmentBasicInfo = {
  title: string,
  description?: string,
  topicIds: number[],
  knowledgePointIds?: number[],
  libraryNames: string[],
  subjectId: number,
  classInfos: ClassroomInfoResponse[],
  dueDate: Date,
  questionInfoList?: QuestionInfoExt[],
  language: string,
  shareTeacherIds?: string[],
}

function checkState(state: AssignmentBasicInfo, t: Function): boolean {
  if (!state.title || state.title.length === 0) {
    alert(t("teacher.alert") + t("common.title"));
    return false;
  }
  if (state.classInfos.length === 0) {
    alert(t("teacher.alert") + t("common.class"));
    return false;
  }
  if (state.topicIds.length === 0) {
    alert(t("teacher.alert") + t("common.unit"));
    return false;
  }
  if (!state.knowledgePointIds || state.knowledgePointIds.length === 0) {
    alert(t("teacher.alert") + "知识点");
    return false;
  }
  if (!state.subjectId || state.subjectId <= 0) {
    alert(t("teacher.alert") + t("common.subject"));
    return false;
  }
  if (!state.dueDate) {
    alert(t("teacher.alert") + t("common.due"));
    return false;
  }
  if (!state.libraryNames) {
    alert(t("teacher.alert") + t("teacher.selectlibrary"));
    return false;
  }
  if (!state.language) {
    alert(t("teacher.alert") + t("common.language"));
    return false;
  }
  return true;
}

function AssignmentPage({
  t,
  i18n,
  ...props
}: WithTranslation) {
  const { getCurrentUser } = useAuth();
  let [classInfoIds, setClassInfoIds] = React.useState<string[]>([])
  let [selectedKnowledgePointIds, setSelectedKnowledgePointIds] = React.useState<string[]>([])
  let [libraryTypes, setLibraryTypes] = React.useState<string[]>([])
  let [language, setLanguage] = React.useState<string>('zh-HK');
  let [subjectId, setSubjectId] = React.useState<number>(0)
  const [dueDate, setDueDate] = React.useState<Date>(new Date())
  const [dueTime, setDueTime] = React.useState<Date>(new Date(0, 0, 0, 23, 59, 59))
  let [title, setTitle] = React.useState<string>("")
  let [description, setDescription] = React.useState<string>()
  const [pressList, setPressList] = React.useState<string[]>([])
  const [drafts, setDrafts] = React.useState<HomeworkDraft[]>([]);
  const [showDraftsDialog, setShowDraftsDialog] = React.useState(false);
  const [showAISelectDialog, setShowAISelectDialog] = React.useState(false);
  const [isAISelecting, setIsAISelecting] = React.useState(false);
  const [shareTeacherIds, setShareTeacherIds] = React.useState<string[]>([]);
  const [teacherOptions, setTeacherOptions] = React.useState<{ label: string; value: string }[]>([]);
  const navigate = useNavigate();

  const getRandomTypeCounts = (total: number) => {
    const mcqCount = Math.round(total * 0.7);
    return { BFQ: 0, MCQ: mcqCount, FRQ: total - mcqCount };
  };

  const handleAISelectConfirm = async () => {
    const state = getState();
    if (!checkState(state, t)) {
      setShowAISelectDialog(false);
      return;
    }

    if (!state.knowledgePointIds || state.knowledgePointIds.length === 0) {
      toast.error(t('common.fail'));
      return;
    }

    const selectedKnowledgePoints = (knowledgePointData?.data?.knowledgePoints || []).filter((kp) =>
      state.knowledgePointIds!.includes(kp.id!),
    );
    if (selectedKnowledgePoints.length === 0) {
      toast.error(t('common.fail'));
      return;
    }

    const totalQuestionCount = 3 + Math.floor(Math.random() * 8);
    const typeCounts = getRandomTypeCounts(totalQuestionCount);
    const typeQueue: Array<'BFQ' | 'MCQ' | 'FRQ'> = [
      ...Array.from({ length: typeCounts.BFQ }, () => 'BFQ' as const),
      ...Array.from({ length: typeCounts.MCQ }, () => 'MCQ' as const),
      ...Array.from({ length: typeCounts.FRQ }, () => 'FRQ' as const),
    ];

    setIsAISelecting(true);
    setShowAISelectDialog(false);

    const subjectName = getSubjectDisplayName(subjectInfos, state.subjectId);
    if (!subjectName) {
      toast.error(t('common.loading') || 'Subject data is loading, please wait.');
      setIsAISelecting(false);
      return;
    }

    try {
      const generatedQuestions: QuestionInfoExt[] = [];
      const randomGrade = 7 + Math.floor(Math.random() * 2); // 7 or 8

      for (const currentType of typeQueue) {
        const randomDifficultyNumber = 1 + Math.floor(Math.random() * 3); // 1,2,3

        // 随机选一个 knowledgePoint，然后找到它所属的 Topic
        const randomKnowledgePoint = selectedKnowledgePoints[
          Math.floor(Math.random() * selectedKnowledgePoints.length)
        ];
        const topicMtrId = randomKnowledgePoint.mtrId?.split('-')[0];
        const randomTopic = topicData?.data?.topicInfos?.find(
          (topic) => topic.mtrId === topicMtrId
        );
        const randomTopicId = randomTopic?.id;


        const generateRsp = await generateQuestion({
          chatId: getCurrentUser()!.id!.toString(),
          prompt: JSON.stringify({
            grade: gradeNumberToText(randomGrade, i18n) || '中學一年級',
            questionType: currentType,
            difficulty: difficultyLevelToText(randomDifficultyNumber, i18n) || '中等',
            unit: [randomTopic?.name || ''],
            subject: subjectName,
            objectives: randomKnowledgePoint.name || '',
          }),
        });

        if (
          generateRsp.code !== 200 ||
          !generateRsp.data?.question ||
          !generateRsp.data?.answer ||
          !generateRsp.data?.questionEn ||
          !generateRsp.data?.answerEn
        ) {
          throw new Error(generateRsp.message || 'generateQuestion failed');
        }

        const createRsp = await createQuestionInfo({
          model: JSON.stringify({
            question: generateRsp.data.question,
            answer: generateRsp.data.answer,
            questionEn: generateRsp.data.questionEn,
            answerEn: generateRsp.data.answerEn,
            grade: randomGrade,
            questionType: currentType,
            originName: getCurrentUser()!.schoolName,
            originType: 'school',
            locale: "b",
            difficulty: randomDifficultyNumber,
            topicIds: [randomTopicId],
            subjectId: state.subjectId,
            knowledgePointIds: [randomKnowledgePoint.id],
            version: 0,
            scope: 2,
          }),
          questionImages: [],
          standardAnswerImages: [],
          answerImages: [],
        });

        if (createRsp.code !== 200 || !createRsp.data?.id) {
          throw new Error(createRsp.message || 'createQuestionInfo failed');
        }

        generatedQuestions.push({
          id: createRsp.data.id,
          questionText: generateRsp.data.question,
          standardAnswer: generateRsp.data.answer,
          questionTextEn: generateRsp.data.questionEn,
          standardAnswerEn: generateRsp.data.answerEn,
          grade: randomGrade,
          questionType: currentType,
          originName: getCurrentUser()!.schoolName,
          originType: 'school',
          locale: 'b',
          difficultyLevel: randomDifficultyNumber,
          creatorId: getCurrentUser()!.email,
          topicIds: [randomTopicId],
          topicNames: [randomTopic?.name || ''],
          knowledgePointIds: [randomKnowledgePoint.id!],
          knowledgeNames: [randomKnowledgePoint.name || ''],
        } as QuestionInfoExt);
      }

      navigate(ROUTE.TEACHER.HOMEWORK_AI_EDIT_RESULT, {
        state: {
          ...state,
          questionInfoList: generatedQuestions,
        }
      });
    } catch (err) {
      console.error('AI instant select flow failed', err);
      toast.error(t('common.fail'));
    } finally {
      setIsAISelecting(false);
    }
  };

  const { data, error, isLoading } = useGetAllClassInfo({ schoolName: getCurrentUser()!.schoolName });
  const { data: topicData, error: topicError, isLoading: topicLoadding } = useGetTopicList({ teacherId: getCurrentUser()!.id, schoolName: getCurrentUser()!.schoolName });
  const { data: knowledgePointData, error: knowledgePointError, isLoading: knowledgePointLoading } = useGetKnowledgePointList({
    teacherId: getCurrentUser()!.id,
    schoolName: getCurrentUser()!.schoolName,
    topicId: ''
  });
  const { subjectInfos } = useSubjectList();

  if (error || topicError || knowledgePointError) {
    console.error("Error fetching class info, topic list, or knowledge points", error, topicError, knowledgePointError);
  }

  useEffect(() => {
    if (!isLoading && data && data.data && data.data.classroomInfoResponseList.length === 0) {
      toast(t('teacher.noclasswarning'));
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (subjectId === 0 && subjectInfos.length > 0) {
      setSubjectId(subjectInfos[0].id!);
    }
  }, [subjectInfos, subjectId]);

  useEffect(() => {
    getSchoolPress({
      schoolName: getCurrentUser()!.schoolName,
    }).then((rsp) => {
      if (rsp.code === 200 && rsp.data) {
        setPressList(rsp.data);
      } else {
        console.log('getSchoolPress empty ');
      }
    }).catch((err) => {
      console.log('getSchoolPress error ', err);
    });
  }, [])

  // Load saved drafts on mount
  useEffect(() => {
    const loadedDrafts = getHomeworkDrafts();
    setDrafts(loadedDrafts);
  }, []);

  // Load teacher list for sharing
  useEffect(() => {
    const currentUser = getCurrentUser()!;
    listTeachersBySchool({ schoolName: currentUser.schoolName }).then((rsp) => {
      if (rsp.code === 200 && rsp.data?.teachers) {
        const options = rsp.data.teachers
          .filter((t: any) => t.id !== currentUser.id)
          .map((t: any) => ({ label: t.name || t.email || `ID:${t.id}`, value: String(t.id) }));
        setTeacherOptions(options);
      }
    }).catch(console.error);
  }, [getCurrentUser]);

  // 构建级联选择器的选项数据
  const cascadingOptions = React.useMemo((): CascadingOption[] => {
    if (!topicData?.data?.topicInfos || !knowledgePointData?.data?.knowledgePoints) {
      return [];
    }

    return topicData.data.topicInfos.map(topic => ({
      label: isEN(i18n) ? (topic.enName || topic.name!) : topic.name!,
      value: topic.id!.toString(),
      children: knowledgePointData.data.knowledgePoints
        .filter(kp => kp.mtrId && kp.mtrId.startsWith(topic.mtrId + '-'))
        .map(kp => ({
          label: isEN(i18n) ? (kp.enName || kp.name!) : kp.name!,
          value: kp.id!.toString(),
        }))
    })).filter(option => option.children && option.children.length > 0);
  }, [topicData, knowledgePointData, i18n]);

  // 从选中的知识点中提取对应的主题 ID
  const getTopicIdsFromKnowledgePoints = React.useMemo(() => {
    if (!knowledgePointData?.data?.knowledgePoints || !topicData?.data?.topicInfos) {
      return [];
    }

    const selectedKPs = knowledgePointData.data.knowledgePoints.filter(kp =>
      selectedKnowledgePointIds.includes(kp.id!.toString())
    );

    const topicIds = new Set<string>();
    selectedKPs.forEach(kp => {
      if (kp.mtrId) {
        // 从 "N1-1" 提取 "N1"
        const topicMtrId = kp.mtrId.split('-')[0];
        const topic = topicData.data.topicInfos.find(t => t.mtrId === topicMtrId);
        if (topic) {
          topicIds.add(topic.id!.toString());
        }
      }
    });

    return Array.from(topicIds);
  }, [selectedKnowledgePointIds, knowledgePointData, topicData]);

  const getState = useCallback((): AssignmentBasicInfo => {
    let classInfos: ClassroomInfoResponse[] = [];
    classInfoIds.forEach((id) => {
      const result = data?.data?.classroomInfoResponseList.find((classInfo) => { return classInfo.id.toString() === id; });
      if (result) {
        classInfos.push(result);
      }
    })

    const topicIds = getTopicIdsFromKnowledgePoints;
    const knowledgePointIds = selectedKnowledgePointIds;

    console.log('NavigateToNextPage', title, description, topicIds, knowledgePointIds, classInfos, dueDate, classInfoIds);
    dueDate.setHours(dueTime.getHours());
    dueDate.setMinutes(dueTime.getMinutes());
    dueDate.setSeconds(dueTime.getSeconds());

    let libraryNames = [];
    const userSchoolName = getCurrentUser()!.schoolName;
    for (let type of libraryTypes) {
      switch (type) {
        case 'AI': libraryNames.push(...QuestionOrigin.filter((value) => value.originType === 'AI').map((value) => value.originName)); break;
        case 'Press': libraryNames.push(...QuestionOrigin.filter((value) => (value.originType === 'Press' && userSchoolName === 'Baptist Wing Lung Secondary School')).map((value) => value.originName)); break;
        case 'OTHERS': libraryNames.push(...QuestionOrigin.filter((value) => value.originType === 'School' && value.originName !== userSchoolName).map((value) => value.originName)); break;
        default: libraryNames.push(type);
      }
    }

    return {
      title,
      description,
      topicIds: topicIds.map((id) => parseInt(id)),
      knowledgePointIds: knowledgePointIds.map((id) => parseInt(id)),
      libraryNames,
      subjectId,
      classInfos,
      dueDate,
      language,
      shareTeacherIds,
    };
  }, [title, description, getTopicIdsFromKnowledgePoints, selectedKnowledgePointIds, libraryTypes, subjectId, classInfoIds, dueDate, dueTime, data, language, shareTeacherIds]);

  return (
    <ContentLayout title={t("teacher.assign")}>
      <div className="relative flex flex-col gap-6  p-10 bg-muted mt-2 ml-2 rounded-2xl">
        <Button
          variant="outline"
          className="absolute top-4 right-4"
          onClick={() => setShowDraftsDialog(true)}
          disabled={drafts.length === 0}
        >
          {t('teacher.draftbox')} {drafts.length > 0 && `(${drafts.length})`}
        </Button>
        {isLoading || isAISelecting ? (
          <div className="flex items-center justify-center py-20">
            <LoadingOverlay isLoading={true} />
          </div>
        ) : (
        <>
        <div className="flex items-center justify-center px-50 pt-5">
          <Step number={1} label={t("teacher.step1")} isActive={true} isCompleted={false} />
          <StepConnector isCompleted={false} />
          <Step number={2} label={[t("teacher.sysedit"), t("common.optional")].join('')} isActive={false} isCompleted={false} />
          <StepConnector isCompleted={false} />
          <Step number={3} label={[t("teacher.manualedit"), t("teacher.publish")].join(',')} isActive={false} isCompleted={false} />
        </div>
        <div className="flex flex-col items-center gap-2 text-center">
          <form className="overflow-auto" onSubmit={(e) => { e.preventDefault(); }}>
            <div className="grid gap-4 py-4 p-2">
              <div className="grid grid-cols-4 items-center gap-4">

                <Label htmlFor="title" className="text-right">
                  {t("common.title")}
                </Label>
                <Input name="title" className="col-span-3" type="text" onChange={(e) => { setTitle(e.target.value) }} required />
                <Label htmlFor="description" className="text-right">
                  {t("common.description")}
                </Label>
                <Input name="description" className="col-span-3" onChange={(e) => { setDescription(e.target.value) }} type="text" />

                <Label htmlFor="subject" className="text-right">
                  {t("common.subject")}
                </Label>

                <Select value={subjectId.toString()} onValueChange={value => setSubjectId(Number(value))}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {subjectInfos.map((label) => (
                        <SelectItem key={label.id} value={label.id!.toString()}>
                          {isEN(i18n) ? (label.enName || label.name) : label.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>

                <Label htmlFor="topics" className="text-right">
                  {t("common.unit")}
                </Label>
                <CascadingSelect
                  className="col-span-3"
                  options={cascadingOptions}
                  value={selectedKnowledgePointIds}
                  onChange={setSelectedKnowledgePointIds}
                  placeholder={t('component.selectknowledgepoint')}
                />

                <Label htmlFor="library" className="text-right">
                  {t("teacher.selectlibrary")}
                </Label>
                <MultiSelect
                  className="col-span-3"
                  options={[
                    ...pressList.map((press) => {
                      for (const p of Press) {
                        if (p.nameEn === press) {
                          return { label: isEN(i18n) ? press : p.name, value: press }
                        }
                      }
                      throw new Error('Press not found');
                    }),
                    { label: t('teacher.myschool'), value: getCurrentUser()!.schoolName },
                    { label: 'CE/DSE', value: 'CE/DSE' },
                    { label: 'TSA', value: 'TSA' },
                    { label: t('teacher.aiquestion'), value: 'AI' },
                    { label: t('teacher.others'), value: 'OTHERS' },
                  ].map((option) => { return { value: option.value, label: option.label } })}
                  onValueChange={setLibraryTypes}
                  defaultValue={libraryTypes}
                  placeholder=""
                  variant="default"
                />

                <Label htmlFor="language" className="text-right">
                  {t("common.language")}
                </Label>
                <Select defaultValue={language} onValueChange={setLanguage}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="zh-HK">{t("common.zh-HK")}</SelectItem>
                      <SelectItem value="en">{t("common.en")}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>

                <Label htmlFor="classroom" className="text-right">
                  {t("common.class")}
                </Label>
                <MultiSelect
                  className="col-span-3"
                  options={[...(data?.data?.classroomInfoResponseList ?? [])].sort((a, b) => a.name.localeCompare(b.name)).map((classInfo) => { return { value: classInfo.id.toString(), label: classInfo.name } })}
                  onValueChange={setClassInfoIds}
                  defaultValue={classInfoIds}
                  placeholder=""
                  variant="default"
                />

                <Label htmlFor="shareWith" className="text-right">
                  {t("teacher.shareWith")}
                  {' '}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CircleHelp className="h-4 w-4 text-muted-foreground cursor-help inline align-text-bottom" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{t('teacher.shareWithTooltip')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <MultiSelect
                  className="col-span-3"
                  options={teacherOptions}
                  onValueChange={setShareTeacherIds}
                  defaultValue={shareTeacherIds}
                  placeholder={t("teacher.shareWith")}
                  variant="default"
                />

                <Label htmlFor="dueDate" className="text-right">
                  {t("common.due")}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "col-span-3 justify-start text-left font-normal bg-muted hover:bg-muted",
                        !dueDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon />
                      {dueDate ? format(dueDate, "PPP") : <span>Pick a dueDate</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={(date) => { if (date) setDueDate(date); }}
                      initialFocus
                    />
                    <PopoverClose className='-z-10 absolute left-0 top-0 w-100 h-100'>123</PopoverClose>
                  </PopoverContent>
                </Popover>

                <Label htmlFor="time-picker" className="text-right">
                </Label>
                <Input
                  type="time"
                  id="time-picker"
                  step="1"
                  defaultValue="23:59:59"
                  onChange={(event) => {
                    const newTimeString = event.target.value;
                    if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(newTimeString)) return;
                    // Combine with a placeholder date (e.g., today's date)
                    const today = new Date();
                    const parts = newTimeString.split(':').map(Number);
                    const [hours, minutes, seconds] = [parts[0], parts[1], parts[2] ?? 0];
                    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) return;
                    today.setHours(hours, minutes, seconds, 0); // Set hours, minutes, seconds, milliseconds

                    setDueTime(today);
                  }}
                  className="col-span-3 bg-muted appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                />

                <Label htmlFor="time-picker" className="text-right">
                </Label>
                <span className='col-span-3 text-red-400 text-sm'>*{t('teacher.assignmenttips')}</span>
                <span className='col-span-4 text-gray-400 text-sm'>{t('teacher.assignmenttimereminder')}</span>
              </div>
            </div>

            <div className='flex flex-wrap items-center justify-center gap-3 sm:gap-4'>
              <Label className="mr-1">{t("teacher.next")}</Label>
              <NavLink to={ROUTE.TEACHER.HOMEWORK_AI_EDIT} state={getState()}>
                <Button variant="default" className="bg-primary" onClick={(e) => {
                  if (!checkState(getState(), t)) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    return;
                  }
                }}>{t("teacher.sysedit")}</Button>
              </NavLink>
              <Label className="mx-1 text-muted-foreground">{t("common.or")}</Label>
              <NavLink to={ROUTE.TEACHER.HOMEWORK_MANUAL_EDIT} state={getState()}>
                <Button variant="default" className="bg-primary" onClick={(e) => {
                  if (!checkState(getState(), t)) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    return;
                  }
                }}>{t("teacher.manualedit")}</Button>
              </NavLink>
              <Label className="mx-1 text-muted-foreground">{t("common.or")}</Label>
              <Button
                type="button"
                variant="default"
                className="bg-primary"
                loading={isAISelecting}
                onClick={() => {
                  if (!checkState(getState(), t)) {
                    return;
                  }
                  setShowAISelectDialog(true);
                }}
              >
                {t('teacher.aiinstantselect')}
              </Button>
            </div>

          </form>
        </div>
        </>
        )}
      </div>

      <Dialog open={showAISelectDialog} onOpenChange={setShowAISelectDialog}>
        <DialogContent className="sm:max-w-[560px]" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{t('teacher.aiinstantselect')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">{t('teacher.aiinstantselectwarning')}</p>
          <DialogFooter className="mt-2 gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setShowAISelectDialog(false)}>
              {t('component.cancel')}
            </Button>
            <Button type="button" className="bg-[#9DC77A]" onClick={handleAISelectConfirm} loading={isAISelecting}>
              {t('component.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drafts Dialog */}
      <Dialog open={showDraftsDialog} onOpenChange={setShowDraftsDialog}>
        <DialogContent className="max-w-2xl sm:max-w-2xl md:max-w-3xl lg:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('teacher.saveddrafts')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-400">{t('teacher.draftnote')}</p>
          <ScrollArea className="h-[400px]">
            <div className="flex flex-col gap-2 pr-4">
              {drafts.map((draft) => (
                <div key={draft.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">{draft.title || t('teacher.untitled') || 'Untitled'}</span>
                    <span className="text-sm text-gray-500">
                      {t('common.due')}: {format(new Date(draft.dueDate), 'PPP')}
                    </span>
                    {draft.description && (
                      <span className="text-sm text-gray-500">
                        {t('common.description')}: {draft.description}
                      </span>
                    )}
                    {draft.classInfos && draft.classInfos.length > 0 && (
                      <span className="text-sm text-gray-500">
                        {t('common.class')}: {draft.classInfos.map(c => c.name).join(', ')}
                      </span>
                    )}
                    {draft.libraryNames && draft.libraryNames.length > 0 && (
                      <span className="text-sm text-gray-500">
                        {t('teacher.selectlibrary')}: {draft.libraryNames.join(', ')}
                      </span>
                    )}
                    {draft.language && (
                      <span className="text-sm text-gray-500">
                        {t('common.language')}: {draft.language === 'en' ? t('common.en') : t('common.zh-HK')}
                      </span>
                    )}
                    {draft.questionInfoList && draft.questionInfoList.length > 0 && (
                      <span className="text-sm text-gray-500">
                        {t('common.question')}: {draft.questionInfoList.length}
                      </span>
                    )}
                    {draft.questionInfoList && draft.questionInfoList.length > 0 && (() => {
                      // Extract unique topic names from all questions
                      const uniqueTopics = [...new Set(
                        draft.questionInfoList
                          .flatMap(q => (q as any).topicNames || [])
                          .filter(Boolean)
                      )];
                      return uniqueTopics.length > 0 ? (
                        <span className="text-sm text-gray-500">
                          {t('common.topic')}: {uniqueTopics.join(', ')}
                        </span>
                      ) : null;
                    })()}
                    <span className="text-xs text-gray-400">
                      {t('teacher.savedat') || 'Saved at'}: {format(new Date(draft.savedAt), 'PPp')}
                    </span>
                    <span className="text-xs text-orange-500">
                      {t('teacher.expiresat') || 'Expires at'}: {format(new Date(draft.expiresAt), 'PPp')}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      className="bg-primary hover:bg-primary/90 cursor-pointer"
                      onClick={() => {
                        // Navigate to the appropriate page based on where the draft was saved
                        const route = draft.currentPage === 'aieditresult'
                          ? ROUTE.TEACHER.HOMEWORK_AI_EDIT_RESULT
                          : ROUTE.TEACHER.HOMEWORK_MANUAL_EDIT;
                        setShowDraftsDialog(false);
                        // 兼容旧草稿：有 subjectName 但没有 subjectId 时，从科目列表反查
                        const resolvedSubjectId = draft.subjectId ?? subjectInfos.find(s =>
                          s.name === draft.subjectName || s.enName === draft.subjectName ||
                          (draft.subjectName && s.enName?.toLowerCase().includes(draft.subjectName.toLowerCase()))
                        )?.id ?? 4;
                        navigate(route, {
                          state: {
                            title: draft.title,
                            description: draft.description,
                            topicIds: draft.topicIds,
                            knowledgePointIds: draft.knowledgePointIds,
                            libraryNames: draft.libraryNames,
                            subjectId: resolvedSubjectId,
                            classInfos: draft.classInfos,
                            dueDate: new Date(draft.dueDate),
                            questionInfoList: draft.questionInfoList,
                            language: draft.language,
                            shareTeacherIds: draft.shareTeacherIds,
                            draftId: draft.id, // Pass draft ID for cleanup on publish
                          }
                        });
                      }}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      {t('teacher.resume') || 'Resume'}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        removeHomeworkDraft(draft.id);
                        setDrafts(drafts.filter(d => d.id !== draft.id));
                        toast.success(t('teacher.draftdeleted') || 'Draft deleted');
                      }}
                      className="cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </ContentLayout >
  );
};

export default withTranslation()(AssignmentPage);